import React, { useMemo, useState, useEffect } from "react";
import { Column, Pie } from "@ant-design/plots";
import { useWindowWidth } from "../../../../hooks/useWindowWidth";
import { Productionform } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { getDonutHeight, BAR_PALETTE, DONUT_COLORS } from "../../../../utils/chartConfig";
import { formatWhole, formatPercentage } from "../../../../utils/numberFormat";
import "./TableGraf.scss";
import { EmptyChartMessage } from "../EmptyChartMessage";

const productionController = new Productionform();

async function getProductionValueByType(productionCtrl, accessToken, site, year, type) {
  try {
    const mode =
      type === "VPS" ? "standardized_production" : "standardized_billing";
    const response = await productionCtrl.getTotalBaselineByYearAndSite(
      accessToken,
      site,
      year,
      mode
    );
    return response.total;
  } catch (error) {
    console.error("Error obteniendo valor de producción:", error);
    return 0;
  }
}

// Últimos 5 años × VPS/VFS — misma lógica que hoy usa el useEffect de
// TableGraf para armar `productionValues` (10 llamadas, sin optimizar,
// replicado tal cual para el PDF).
export async function fetchProductionAnnualTotals(productionCtrl, accessToken, site, baseline) {
  if (!baseline || !site) return { VPS: {}, VFS: {} };

  const currentYear = new Date().getFullYear();
  const lastFiveYears = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const vpsResults = await Promise.all(
    lastFiveYears.map(async (year) => {
      const yearStr = String(year);
      const value = await getProductionValueByType(productionCtrl, accessToken, site, yearStr, "VPS");
      return { [yearStr]: value };
    })
  );
  const vfsResults = await Promise.all(
    lastFiveYears.map(async (year) => {
      const yearStr = String(year);
      const value = await getProductionValueByType(productionCtrl, accessToken, site, yearStr, "VFS");
      return { [yearStr]: value };
    })
  );

  return {
    VPS: vpsResults.reduce((acc, obj) => ({ ...acc, ...obj }), {}),
    VFS: vfsResults.reduce((acc, obj) => ({ ...acc, ...obj }), {}),
  };
}

export function buildProductionData(baseline, productionValues) {
  if (!baseline) return [];
  if (!productionValues?.VPS || Object.keys(productionValues.VPS).length === 0) return [];

  const currentYear = new Date().getFullYear();
  const lastFiveYears = Array.from({ length: 5 }, (_, i) => currentYear - i).reverse();

  const rawData = [];
  lastFiveYears.forEach((year) => {
    const yearStr = String(year);
    const productionType = baseline[yearStr] || "VPS";
    const value = productionValues[productionType]?.[yearStr] ?? 0;
    if (value > 0) {
      rawData.push({ year: yearStr, value, type: productionType });
    }
  });

  return rawData;
}

export function buildWorkersData(totales) {
  if (!totales || !totales["quantity_workers_kpis"]) return [];

  const currentYear = new Date().getFullYear();
  const lastFiveYears = Array.from({ length: 5 }, (_, i) => currentYear - i).reverse();

  return lastFiveYears
    .map((year) => {
      const yearStr = String(year);
      const workerData = totales["quantity_workers_kpis"][yearStr];
      return { year: yearStr, value: workerData?.total || 0 };
    })
    .filter((item) => item.value > 0);
}

// `type` queda fijo en español porque es la clave interna que matchea
// contra DONUT_COLORS.social (colores) — el texto que se muestra (pills,
// tooltip) se traduce por separado con este mapa, sin tocar `type`.
export const GENDER_TYPE_LABEL_KEYS = {
  Mujeres: "gender_female",
  Hombres: "gender_male",
  Otros: "gender_other",
};

export function buildGenderData(totales, filters) {
  if (!totales) return [];

  const year = filters?.year || new Date().getFullYear();
  const yearStr = String(year);

  const totalWorkers = totales["quantity_workers_kpis"]?.[yearStr]?.total || 0;
  const femaleProportion = totales["average_female_total_kpis"]?.[yearStr]?.total || 0;
  const maleProportion = totales["average_male_total_kpis"]?.[yearStr]?.total || 0;
  const femaleLeadershipProportion =
    totales["average_female_workers_leadership_positions_kpis"]?.[yearStr]?.total || 0;

  const femaleTotal = femaleProportion * totalWorkers;
  const maleTotal = maleProportion * totalWorkers;
  const otherGenders = totalWorkers - (femaleTotal + maleTotal);

  const data = [
    {
      type: "Mujeres",
      value: femaleTotal,
      percentage: totalWorkers > 0 ? (femaleTotal / totalWorkers) * 100 : 0,
      leadershipPercentage: femaleLeadershipProportion * 100,
    },
    {
      type: "Hombres",
      value: maleTotal,
      percentage: totalWorkers > 0 ? (maleTotal / totalWorkers) * 100 : 0,
      leadershipPercentage: totalWorkers > 0 ? (1 - femaleLeadershipProportion) * 100 : 0,
    },
  ];

  if (otherGenders > 0.1) {
    data.push({
      type: "Otros",
      value: otherGenders,
      percentage: totalWorkers > 0 ? (otherGenders / totalWorkers) * 100 : 0,
      leadershipPercentage: 0,
    });
  }

  return data.filter((item) => item.value > 0);
}

const TableGraf = ({ filters, totales, baseline, valueBaseline, site, t }) => {
  const windowWidth = useWindowWidth();
  const { accessToken } = useAuth();

  // Estado para los valores de producción calculados (VPS y VFS)
  const [productionValues, setProductionValues] = useState({
    VPS: {},
    VFS: {},
  });

  // Efecto para calcular AMBOS valores (VPS y VFS) al cargar
  useEffect(() => {
    fetchProductionAnnualTotals(productionController, accessToken, site, baseline).then(
      setProductionValues
    );
  }, [baseline, site, accessToken]);

  // Memoizar configuración responsive para evitar recalcular en cada render
  const responsiveConfig = useMemo(() => {
    const isMobile = windowWidth <= 768;
    const isSmallMobile = windowWidth <= 480;
    const isTablet = windowWidth > 768 && windowWidth <= 992;
    const isNotebook = windowWidth >= 1024 && windowWidth <= 1500;

    return {
      isMobile,
      isSmallMobile,
      isTablet,
      isNotebook,
      // Tamaños de fuente consistentes
      fontSize: {
        large: isSmallMobile ? 12 : isMobile ? 14 : 16,
        medium: isSmallMobile ? 10 : isMobile ? 11 : 12,
        small: isSmallMobile ? 9 : isMobile ? 10 : 11,
      },
      // Alturas de gráficos
      chartHeight: {
        donut: getDonutHeight(isNotebook, isMobile),
        bar: isSmallMobile ? 250 : isMobile ? 280 : 300,
      },
    };
  }, [windowWidth]);

  // ================================
  // 🟦 Preparar datos de producción CON NORMALIZACIÓN
  // ================================
  const productionData = useMemo(() => {
    const rawData = buildProductionData(baseline, productionValues);

    // ✅ Normalización
    if (rawData.length === 0) return [];

    const maxValue = Math.max(...rawData.map((d) => d.value));

    const chartData = rawData.map((d) => {
      const isSmall = d.value < 1;
      return {
        year: d.year,
        type: d.type,
        displayValue: isSmall ? maxValue * 0.1 : d.value,
        realValue: d.value,
        isSmall: isSmall,
      };
    });

    //console.log("📊 Chart Data Production normalizado:", chartData);

    return chartData;
  }, [productionValues, baseline]);

  // ================================
  // [CÓDIGO ANTERIOR COMENTADO] Production Data sin normalización
  // ================================
  /* const productionData = useMemo(() => {
    console.log("🔄 useMemo productionData ejecutándose");
    console.log("  - baseline:", baseline);
    console.log("  - productionValues:", productionValues);

    if (!baseline) {
      console.log("  ❌ No hay baseline, retornando array vacío");
      return [];
    }

    if (!productionValues.VPS || Object.keys(productionValues.VPS).length === 0) {
      console.log("  ❌ No hay productionValues, retornando array vacío");
      return [];
    }

    const currentYear = new Date().getFullYear();
    const lastFiveYears = Array.from({ length: 5 }, (_, i) => currentYear - i).reverse();

    const data = [];

    lastFiveYears.forEach((year) => {
      const yearStr = String(year);
      const productionType = baseline[yearStr] || "VPS";
      const value = productionValues[productionType]?.[yearStr] ?? 0;

      console.log(`  - Año ${yearStr}: tipo=${productionType}, valor=${value}`);

      if (value > 0) {
        data.push({
          year: yearStr,
          value: value,
          type: productionType,
        });
      }
    });

    console.log("📊 Production Data para gráfico:", data);
    return data;
  }, [productionValues, baseline]); */

  // ================================
  // 🟦 Preparar datos de trabajadores CON NORMALIZACIÓN
  // ================================
  const workersData = useMemo(() => {
    const rawData = buildWorkersData(totales);

    // ✅ Normalización
    if (rawData.length === 0) return [];

    const maxValue = Math.max(...rawData.map((d) => d.value));

    const chartData = rawData.map((d) => {
      const isSmall = d.value < 1;
      return {
        year: d.year,
        displayValue: isSmall ? maxValue * 0.1 : d.value,
        realValue: d.value,
        isSmall: isSmall,
      };
    });

    //console.log("📊 Chart Data Workers normalizado:", chartData);

    return chartData;
  }, [totales]);

  // ================================
  // [CÓDIGO ANTERIOR COMENTADO] Workers Data sin normalización
  // ================================
  /* const workersData = useMemo(() => {
    if (!totales || !totales["quantity_workers_kpis"]) return [];

    const currentYear = new Date().getFullYear();
    const lastFiveYears = Array.from({ length: 5 }, (_, i) => currentYear - i).reverse();

    return lastFiveYears
      .map((year) => {
        const yearStr = String(year);
        const workerData = totales["quantity_workers_kpis"][yearStr];
        const value = workerData?.total || 0;

        return {
          year: yearStr,
          value: value,
        };
      })
      .filter((item) => item.value > 0);
  }, [totales]); */

  // 🔹 Preparar datos de género para el año seleccionado (KPIsoc11, KPIsoc13, KPIsoc14)
  const genderData = useMemo(
    () => buildGenderData(totales, filters),
    [totales, filters]
  );

  // ========================
  // 🎨 COLOR MAPS & PALETTE
  // ========================
  const barPalette = BAR_PALETTE;

  const genderColorMap = DONUT_COLORS.social;

  // ========================
  // 🔄 ESTADOS DE TOGGLE
  // ========================
  const allYearsProduction = useMemo(() => productionData.map(d => d.year), [productionData]);
  const allYearsWorkers    = useMemo(() => workersData.map(d => d.year),    [workersData]);

  const [activeYearsProduction, setActiveYearsProduction] = useState([]);
  const [activeYearsWorkers,    setActiveYearsWorkers]    = useState([]);
  const [hiddenGenders,         setHiddenGenders]         = useState([]);

  useEffect(() => { setActiveYearsProduction(allYearsProduction); }, [allYearsProduction]);
  useEffect(() => { setActiveYearsWorkers(allYearsWorkers); },       [allYearsWorkers]);

  const productionColorMap = useMemo(() => {
    const map = {};
    allYearsProduction.forEach((y, i) => { map[y] = barPalette[i] || "#ccc"; });
    return map;
  }, [allYearsProduction]);

  const workersColorMap = useMemo(() => {
    const map = {};
    allYearsWorkers.forEach((y, i) => { map[y] = barPalette[i] || "#ccc"; });
    return map;
  }, [allYearsWorkers]);

  const filteredProductionData = useMemo(
    () => productionData.filter(d => activeYearsProduction.includes(d.year)),
    [productionData, activeYearsProduction]
  );

  const filteredWorkersData = useMemo(
    () => workersData.filter(d => activeYearsWorkers.includes(d.year)),
    [workersData, activeYearsWorkers]
  );

  const filteredGenderData = useMemo(
    () => genderData.filter(d => !hiddenGenders.includes(d.type)),
    [genderData, hiddenGenders]
  );

  // 🔹 Configuración del gráfico de Producción CON NORMALIZACIÓN
  const productionConfig = {
    data: filteredProductionData,
    xField: "year",
    yField: "displayValue",
    colorField: "year",
    scale: { color: { domain: Object.keys(productionColorMap), range: Object.values(productionColorMap) } },
    autoFit: true,
    height: responsiveConfig.chartHeight.bar,
    label: false,
    legend: false,
    style: {
      maxWidth: 80,
    },
    tooltip: {
      shared: true,
      title: (d) => `Año ${d.year}`,
      items: [
        (datum) => {
          // ✅ Usar realValue
          const value = datum?.realValue ?? datum?.value ?? 0;
          return {
            name: `Producción (${datum.type})`,
            value: formatWhole(value),
          };
        },
      ],
    },
    interactions: [{ type: "active-region" }],
    // `xAxis`/`yAxis` es la API vieja (v1) de @ant-design/plots — la
    // instalada (v2) no la reconoce, quedaba ignorada en silencio (mismo
    // bug ya visto en GEI/Residuos/Efluentes/Combustibles). axis.x/axis.y
    // es la forma correcta.
    axis: {
      x: {
        title: t("year"),
        titleFontSize: responsiveConfig.fontSize.medium,
        labelFontSize: responsiveConfig.fontSize.medium,
        labelAutoRotate: responsiveConfig.isMobile ? false : true,
        labelAutoHide: false,
      },
      y: {
        title: responsiveConfig.isMobile ? t("total") : t("total_production_axis"),
        titleFontSize: responsiveConfig.fontSize.medium,
        labelFontSize: responsiveConfig.fontSize.small,
        labelFormatter: (v) => {
          if (responsiveConfig.isMobile && v >= 1000000) {
            return `${(v / 1000000).toFixed(1)}M`;
          }
          if (responsiveConfig.isMobile && v >= 1000) {
            return `${(v / 1000).toFixed(1)}K`;
          }
          return formatWhole(v);
        },
      },
    },
  };

  // 🔹 Configuración del gráfico de Trabajadores CON NORMALIZACIÓN
  const workersConfig = {
    data: filteredWorkersData,
    xField: "year",
    yField: "displayValue",
    colorField: "year",
    scale: { color: { domain: Object.keys(workersColorMap), range: Object.values(workersColorMap) } },
    autoFit: true,
    height: responsiveConfig.chartHeight.bar,
    label: false,
    legend: false,
    style: {
      maxWidth: 120,
    },
    tooltip: {
      shared: true,
      title: (d) => `Año ${d.year}`,
      items: [
        (datum) => {
          // ✅ Usar realValue
          const value = datum?.realValue ?? datum?.value ?? 0;
          return {
            name: "Trabajadores",
            value: formatWhole(value),
          };
        },
      ],
    },
    // Ver comentario en productionConfig más arriba: axis.x/axis.y es la
    // API correcta (v2), xAxis/yAxis se ignoraba en silencio.
    axis: {
      x: {
        title: t("year"),
        titleFontSize: responsiveConfig.fontSize.medium,
        labelFontSize: responsiveConfig.fontSize.medium,
        labelAutoRotate: responsiveConfig.isMobile ? false : true,
        labelAutoHide: false,
      },
      y: {
        title: responsiveConfig.isMobile ? t("total") : t("total_workers_axis"),
        titleFontSize: responsiveConfig.fontSize.medium,
        labelFontSize: responsiveConfig.fontSize.small,
        labelFormatter: (v) => {
          if (responsiveConfig.isMobile && v >= 1000) {
            return `${(v / 1000).toFixed(1)}K`;
          }
          return formatWhole(v);
        },
      },
    },
    interactions: [{ type: "active-region" }],
  };

  // 🔹 Configuración del gráfico de Donut de Género
  const genderConfig = {
    data: filteredGenderData,
    angleField: "value",
    colorField: "type",
    scale: { color: { domain: Object.keys(genderColorMap), range: Object.values(genderColorMap) } },
    radius: 1,
    innerRadius: 0.6,
    height: responsiveConfig.chartHeight.donut,
    label: false,
    legend: false,
    statistic: {
      title: false,
      content: {
        content: `${t("year")}\n${filters?.year || new Date().getFullYear()}`,
        style: {
          fontSize: responsiveConfig.fontSize.large,
          fontWeight: 300,
          whiteSpace: "pre-line",
        },
      },
    },
    tooltip: {
      shared: true,
      title: (datum) => t(GENDER_TYPE_LABEL_KEYS[datum.type] || datum.type),
      items: [
        (datum) => ({
          name: "Total",
          value: formatWhole(datum?.value),
        }),
        (datum) => ({
          name: "Porcentaje",
          value: formatPercentage(datum?.percentage || 0),
        }),
      ],
    },
    interactions: [{ type: "element-active" }],
  };

  return (
    <div className="socialDashboardGrid">
      {/* Producción */}
      <div className="chartCard">
        <h3 className="cardTitle">
          {t("production_last_5_years")}
        </h3>
        <div className="chartWrapper">
          {!Array.isArray(productionData) || productionData.length === 0 ? (
            <EmptyChartMessage
              message={
                t("complete_at_least_one_period") ||
                "Complete al menos un período para visualizar este indicador"
              }
            />
          ) : (
            <Column {...productionConfig} />
          )}
        </div>
        <div className="pillsRow">
          {allYearsProduction.map((year) => (
            <div
              key={year}
              className="yearPill"
              onClick={() =>
                setActiveYearsProduction((prev) =>
                  prev.includes(year)
                    ? prev.filter((y) => y !== year)
                    : [...prev, year]
                )
              }
              style={{
                backgroundColor: activeYearsProduction.includes(year)
                  ? productionColorMap[year]
                  : "#E5E7EB",
                textDecoration: activeYearsProduction.includes(year)
                  ? "none"
                  : "line-through",
              }}
            >
              {year}
            </div>
          ))}
        </div>
      </div>

      {/* Trabajadores */}
      <div className="chartCard">
        <h3 className="cardTitle">
          {t("workers_last_5_years")}
        </h3>
        <div className="chartWrapper">
          {!Array.isArray(workersData) || workersData.length === 0 ? (
            <EmptyChartMessage
              message={
                t("complete_at_least_one_period") ||
                "Complete al menos un período para visualizar este indicador"
              }
            />
          ) : (
            <Column {...workersConfig} />
          )}
        </div>
        <div className="pillsRow">
          {allYearsWorkers.map((year) => (
            <div
              key={year}
              className="yearPill"
              onClick={() =>
                setActiveYearsWorkers((prev) =>
                  prev.includes(year)
                    ? prev.filter((y) => y !== year)
                    : [...prev, year]
                )
              }
              style={{
                backgroundColor: activeYearsWorkers.includes(year)
                  ? workersColorMap[year]
                  : "#E5E7EB",
                textDecoration: activeYearsWorkers.includes(year)
                  ? "none"
                  : "line-through",
              }}
            >
              {year}
            </div>
          ))}
        </div>
      </div>

      {/* Género */}
      <div className="chartCard">
        <h3 className="cardTitle">
          {t("gender_distribution") }
        </h3>
        <div className="chartWrapper">
          {!Array.isArray(genderData) || genderData.length === 0 ? (
            <EmptyChartMessage
              message={
                t("complete_at_least_one_period") ||
                "Complete al menos un período para visualizar este indicador"
              }
            />
          ) : (
            <Pie {...genderConfig} />
          )}
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            alignItems: "start",
            gap: "8px",
            marginTop: "15px",
            borderTop: "1px solid #000",
            paddingTop: "15px",
            // 130px = mismo min-height que .pillsRow (tableGraf.shared.scss),
            // para que la línea (borderTop) quede a la misma altura que en
            // las cards de barras dentro del mismo chartCard flex-column.
            minHeight: "130px",
          }}
        >
          {genderData.map((item) => {
            const isHidden = hiddenGenders.includes(item.type);
            const color = genderColorMap[item.type] || "#ccc";
            return (
              <div
                key={item.type}
                className="yearPill"
                onClick={() =>
                  setHiddenGenders((prev) =>
                    isHidden
                      ? prev.filter((g) => g !== item.type)
                      : [...prev, item.type]
                  )
                }
                style={{
                  backgroundColor: isHidden ? "#E5E7EB" : color,
                  textDecoration: isHidden ? "line-through" : "none",
                }}
              >
                {t(GENDER_TYPE_LABEL_KEYS[item.type] || item.type)}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TableGraf;
